import { format, parseISO, type Locale } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { Namespace, type TFn } from "@racoon/i18n";
import type { ScheduleForm, WindowForm } from "../model/types";

/** Monday first, as the week reads in Kazakhstan. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const;
const weekdayRank = (dow: number) => (dow + 6) % 7;

// 2026-01-04 is a Sunday, so day 4 + dow lands on that weekday.
export const weekdayName = (dow: number, locale: Locale, pattern = "EEEEEE") =>
  format(new Date(2026, 0, 4 + dow), pattern, { locale });

export const monthName = (month: number, locale: Locale) =>
  format(new Date(2026, month - 1, 1), "LLL", { locale });

export const windowsText = (windows: WindowForm[]) =>
  windows.map(({ from, to }) => `${from}–${to}`).join(", ");

/** The rule in one line: "Пн 06:00–12:00; Ср 1, 3 нед. 08:00–10:00 · месяцы: янв, фев". */
export const describeRules = (form: ScheduleForm, t: TFn, locale: Locale): string => {
  const rule = (() => {
    switch (form.kind) {
      case "weekly":
        return [...form.weekly]
          .sort((a, b) => weekdayRank(a.dow) - weekdayRank(b.dow))
          .map((day) =>
            [
              weekdayName(day.dow, locale),
              day.weeks.length > 0 &&
                t(Namespace.PickupLocation, "schedules.rule.weeks", {
                  weeks: [...day.weeks].sort((a, b) => a - b).join(", "),
                }),
              windowsText(day.windows),
            ]
              .filter(Boolean)
              .join(" "),
          )
          .join("; ");
      case "monthly": {
        // Days sharing their windows read as one group: "1, 15 числа 06:00–12:00".
        const byWindows = new Map<string, number[]>();
        form.monthly.forEach(({ day, windows }) => {
          const key = windowsText(windows);
          byWindows.set(key, [...(byWindows.get(key) ?? []), day]);
        });
        return [...byWindows]
          .map(
            ([windows, days]) =>
              `${t(Namespace.PickupLocation, "schedules.rule.monthDays", { days: days.join(", ") })} ${windows}`,
          )
          .join("; ");
      }
      case "everyNDays":
        return `${t(Namespace.PickupLocation, "schedules.rule.everyNDays", {
          n: form.everyNDays.n,
          from: form.validFrom,
        })} ${windowsText(form.everyNDays.windows)}`;
    }
  })();
  if (form.kind === "everyNDays" || form.months.length === 0) return rule;
  const months = [...form.months]
    .sort((a, b) => a - b)
    .map((month) => monthName(month, locale))
    .join(", ");
  return `${rule} · ${t(Namespace.PickupLocation, "schedules.rule.months", { months })}`;
};

/** "Евроконтейнер 1,1 (3)", as the schedule tab names a container group. */
export const containerGroupName = (cg: ConnectProto.ContainerGroup | undefined, t: TFn) =>
  `${cg?.containerType?.name || t(Namespace.PickupLocation, "schedule.modal.container")} (${cg?.numberOfContainers ?? 0})`;

/** "01.11.2026 — бессрочно". */
export const periodText = (validFrom: string | undefined, validTo: string | undefined, t: TFn) =>
  t(Namespace.PickupLocation, "schedules.period", {
    from: validFrom ? format(parseISO(validFrom), "dd.MM.yyyy") : "—",
    to: validTo
      ? format(parseISO(validTo), "dd.MM.yyyy")
      : t(Namespace.PickupLocation, "schedules.openEnded"),
  });
